import { useState, useEffect, useMemo } from 'react';
import { getValue, setValue } from 'services/idb-store';
import { AnyVariables, UseQueryArgs, UseQueryExecute, UseQueryState, stringifyVariables } from 'urql';
import { compressData, restoreData } from 'utils/offlineQueryMutation';
import useProfileConfig from './useProfileConfig';

type QueryFn<TData, TVariables extends AnyVariables> = (
  options: Omit<UseQueryArgs<TVariables, TData>, 'query'>,
) => [UseQueryState<TData, TVariables>, UseQueryExecute];

type QueryOptions<TData, TVariables extends AnyVariables> = Omit<UseQueryArgs<TVariables, TData>, 'query'>;

type OfflineQueryReturn<TData, TVariables extends AnyVariables> = [UseQueryState<TData, TVariables>, UseQueryExecute];

// Variables are part of the key so one record's saved result is never served for another.
const toStorageKey = (key: string, profileID: string | undefined, variables: AnyVariables) =>
  `cache-v3-${key}-${profileID}-${stringifyVariables(variables ?? {})}`;

const useOfflineQuery = <TData, TVariables extends AnyVariables = AnyVariables>(
  key: string,
  query: QueryFn<TData, TVariables>,
  options: QueryOptions<TData, TVariables> & {
    returnCachedDataWhileFetching?: boolean;
  },
): OfflineQueryReturn<TData, TVariables> => {
  const { profileID } = useProfileConfig();
  const storageKey = toStorageKey(key, profileID, options.variables);
  const [cached, setCached] = useState<{ storageKey: string; data: TData } | undefined>(undefined);
  // Held with its key, so after switching records the previous record's data is never used.
  const cachedData = cached?.storageKey === storageKey ? cached.data : undefined;

  const [result, refresh] = query(options);

  // Load cached data from IndexedDB on mount / key change
  useEffect(() => {
    let isCurrent = true;
    getValue(storageKey).then((stored) => {
      const restoredData = stored ? restoreData(stored) : null;
      if (!isCurrent || !restoredData) return;
      // Network data may have landed first; keep it over the older stored copy.
      setCached((prev) => (prev?.storageKey === storageKey ? prev : { storageKey, data: restoredData as TData }));
    });
    return () => {
      isCurrent = false;
    };
  }, [storageKey]);

  // Persist fresh remote data to IndexedDB
  useEffect(() => {
    if (!result.data || result.fetching) return;
    // urql keeps the previous request's data while paused or refetching, so save it under that request's key.
    const resultStorageKey = result.operation ? toStorageKey(key, profileID, result.operation.variables) : storageKey;
    setCached({ storageKey: resultStorageKey, data: result.data });
    setValue(resultStorageKey, compressData(result.data));
  }, [result, storageKey, key, profileID]);

  const resultToReturn: UseQueryState<TData, TVariables> = useMemo(() => {
    if (!!result.data) {
      // Remote data available — use it directly
      return result;
    } else if (cachedData && result.fetching && options.returnCachedDataWhileFetching) {
      // Still fetching — show cached data marked as stale
      return {
        ...result,
        data: cachedData,
        stale: true,
      };
    } else if (cachedData && !!result.error?.networkError && !result.data) {
      // Network failed — cached data is the best we have
      return {
        ...result,
        data: cachedData,
        error: undefined, // Clear error since we have cached data to show
        stale: false,
        fetching: false,
      };
    } else {
      // No cached data — pass through the remote result as-is
      return result;
    }
  }, [result, cachedData, options.returnCachedDataWhileFetching]);

  return [resultToReturn, refresh];
};

export default useOfflineQuery;
